import {
  faBrakeWarning,
  faShieldXmark,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons'
import { ActionIcon, Button, Flex, Title } from '@mantine/core'
import { useRouter } from 'next/router'

import { useQueryGetInsuranceById } from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import { type CoreInsurance, type ParameterID } from '@flecto/types'
import { Icon, useMediaQuery } from '@flecto/ui'
import { Page } from '@flecto/ui/saas'

import {
  CoveragePeriod,
  CustomerDetails,
  InsuranceNotes,
  PolicyDetails,
  ProductDetails,
} from '..'
import { BadgeInsuranceStatus } from '../../booking-insurances/components/BadgeInsuranceStatus/BadgeInsuranceStatus'
import { useModalConfirmationCancelInsurance } from '../hooks/useModalConfirmationCancelInsurance'

export function PageInsuranceDetails() {
  const { t } = useTranslation('saas', { keyPrefix: 'insurances' })
  const { isMobile } = useMediaQuery()
  const router = useRouter()

  const { openModal } = useModalConfirmationCancelInsurance()

  const { data: insurance, isLoading } = useQueryGetInsuranceById(
    router.query.id as ParameterID
  )

  const insuranceActiveOrWithinClaimPeriod = [
    'active',
    'within_claim_period',
  ].includes(String(insurance?.meta_status))

  return (
    <Page withContainer withLoading={isLoading}>
      <Page.Title>
        <Flex justify='space-between' align='center'>
          <Flex gap={isMobile ? 'sm' : 'md'} align='center'>
            <ActionIcon
              onClick={() => {
                router.push('/insurances')
              }}
              variant='light'
              color='gray'
            >
              <Icon icon={faTimes} size='lg' />
            </ActionIcon>
            <Title order={3}>
              {t('insurances_details.title', {
                replace: { id: insurance?.internal_id },
              })}
            </Title>
            <BadgeInsuranceStatus insurance={insurance} withDot mt={-8} />
          </Flex>

          {insurance?.meta_status === 'scheduled' && (
            <Button
              size='sm'
              variant='default'
              leftSection={!isMobile && <Icon icon={faShieldXmark} size='sm' />}
              c='red'
              style={{ borderColor: 'red' }}
              fw={500}
              onClick={() => {
                openModal({ insurance })
              }}
            >
              {!isMobile ? (
                t('insurances_details.label_cancel_insurance')
              ) : (
                <Icon icon={faShieldXmark} size='sm' />
              )}
            </Button>
          )}

          {insuranceActiveOrWithinClaimPeriod && (
            <Button
              size='sm'
              variant='default'
              leftSection={
                !isMobile && <Icon icon={faBrakeWarning} size='sm' />
              }
              component='a'
              target='_blank'
              c='gray.7'
              href={insurance?.claim_url}
            >
              {!isMobile ? (
                t('insurances_details.label_claims')
              ) : (
                <Icon icon={faBrakeWarning} size='sm' />
              )}
            </Button>
          )}
        </Flex>
      </Page.Title>
      <Page.Content>
        <Flex direction='column' gap='xl'>
          <PolicyDetails insurance={insurance as CoreInsurance} />

          <CustomerDetails insurance={insurance as CoreInsurance} />

          <CoveragePeriod
            startDate={insurance?.start_at || ''}
            endDate={insurance?.end_at || ''}
          />
          <ProductDetails insurance={insurance as CoreInsurance} />
          <InsuranceNotes insurance={insurance as CoreInsurance} />
        </Flex>
      </Page.Content>
    </Page>
  )
}
